import basicProfileChapter from "./chapters/basicProfileChapter";
import graduateProfileChapter from "./chapters/graduateProfileChapter";
import hubIntroductionChapter from "./chapters/hubIntroductionChapter";
import announcementChapter from "./chapters/announcementChapter";
import registryChapter from "./chapters/registryChapter";

/**
 * Every tutorial chapter, in the order they are offered (admin chapter picker, "restart from").
 * To add a tutorial: create a chapter file in ./chapters, add it here, and point the previous
 * chapter's `next` at it.
 *
 * Chapter:
 *   id, title            Shown as "<title> - n of N".
 *   route                Screen name where this chapter's steps are visible (`routes` lists alternatives for the same page).
 *   getRouteParams(ctx)  Optional. Params needed to navigate back to `route` (e.g. the owner's page uid).
 *   followOnHandoff      false when the previous chapter already opened this page elsewhere (e.g. a new tab), so
 *                        finishing it must not navigate this tab.
 *   isEligible(ctx)      Optional. If false when the chapter is reached, the tutorial finishes instead.
 *   next(ctx)            Chapter id to continue with (or null to finish the tutorial).
 *   steps                Ordered list of steps.
 *
 * Step:
 *   id                   Unique within the chapter; persisted so refreshes resume here.
 *   target               SPOTLIGHT_TARGETS id of the real element to highlight, or a list of alternatives (omit to center the card).
 *   title, body, bullets Card copy. `bullets` is [{ label, text }].
 *   placement            'auto' | 'top' | 'bottom' | 'left' | 'right' | 'center'.
 *   interactive          The highlighted element stays clickable; the rest of the page stays blocked.
 *   instruction          Short hint, shown as the only UI on `silent` steps.
 *   silent               Not counted in "n of N", shown as a compact instruction pill, no Back.
 *   requires             { scope, when(signals), hint }: Next stays disabled until true.
 *   waitFor              Transition or [Transition]: advances automatically when the user does something.
 *   optional             On a `waitFor` step: also shows a button (`skipLabel`, default "Skip step") to move on without acting.
 *   completeWhenTargetGone  Leave the step once the target disappears (e.g. a modal that was dismissed): true goes to the
 *                        next step, a string is a `goto` (see Transition). The step shows
 *                        nothing until its target has been found, so a stale step never flashes after a reload.
 *   skipIfTargetMissing  Silently moves on when the target never renders (e.g. a button hidden by a feature flag).
 *   missingAfterMs       How long to wait for the target before treating it as missing (default 1800), for slow-loading pages.
 *   allowBack            false hides Back (for steps tied to a modal that Back would leave open).
 *   skipTo({ userData, getSignals })   Evaluated when leaving the step with Next/Skip: a step id (or '@chapter') to jump to instead of
 *                        the following step. Returns nothing to continue normally.
 *   onAdvance()          Runs when the step is left with Next/Skip, e.g. to close a modal the step is about to skip past.
 *   completion           Final step: shows the completion card instead of a spotlight and marks the tutorial complete straight away.
 *   actionLabel          String, or ({ userData }) => string.
 *
 * Transition: { event | route | signal: { scope }, when(payload), goto }
 *   goto: 'next' (default) | <stepId in this chapter> | '@chapter' (finish this chapter) | '@complete' (finish the whole tutorial)
 */
export const TUTORIAL_CHAPTERS = [basicProfileChapter, graduateProfileChapter, hubIntroductionChapter, announcementChapter, registryChapter];

export const getChapter = (chapterId) => TUTORIAL_CHAPTERS.find((chapter) => chapter.id === chapterId) || null;

export const getFirstChapter = () => TUTORIAL_CHAPTERS[0];
